import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SessionProvider } from '../context/SessionContext';
import { useSession } from '../hooks/useSession';

interface ConditionalSessionProviderProps {
  children: React.ReactNode;
}

/**
 * Conditionally provides SessionProvider and manages initialization flow
 * This component ensures proper sequencing: session -> experiments -> app
 */
export const ConditionalSessionProvider: React.FC<ConditionalSessionProviderProps> = ({ children }) => {
  const location = useLocation();

  // Define route path variables
  const rootPath = process.env.REACT_APP_ROOT_PATH || '';
  const isDefaultRoute = location.pathname === `${rootPath}/` || location.pathname === '/';

  // Only auto-fetch session when NOT on Default route
  const shouldAutoFetch = !isDefaultRoute;

  return (
    <SessionProvider autoFetch={shouldAutoFetch}>
      <InitializedApp>{children}</InitializedApp>
    </SessionProvider>
  );
};

/**
 * Component that ensures proper initialization flow:
 * 1. Session is loaded
 * 2. Experiments are loaded
 * 3. App can proceed with settings and other requests
 */
const InitializedApp: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { sessionId, loading: sessionLoading, experimentsLoaded, experimentsLoading } = useSession();

  // Show loading state while session or experiments are loading
  if (sessionLoading || (sessionId && !experimentsLoaded && experimentsLoading)) {
    return <div></div>;
  }

  return <>{children}</>;
};
